/**
 * API 密钥管理。
 *
 * 三条对客户最要紧的行为，UI 必须讲清楚：
 *   1. **明文只出现一次** —— 创建/轮换弹窗必须让人无法忽略这一点；
 *   2. **轮换有宽限期** —— 新旧密钥同时有效，先部署再淘汰，不必停服；
 *   3. **吊销不可恢复** —— 与「因欠费被挂起」明确区分（后者续费即恢复）。
 */

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/context';
import { ApiError, api, type ApiKey } from '../lib/api';

const ALL_SCOPES = ['bars:read', 'snapshot:read', 'reference:read', 'usage:read'];
const GRACE_CHOICES = ['1h', '24h', '7d'] as const;
type Grace = (typeof GRACE_CHOICES)[number];

function fmt(iso: string | null, dash: string): string {
  return iso ? new Date(iso).toLocaleString() : dash;
}

export default function Keys({ maxKeys }: { maxKeys: number }) {
  const { s } = useI18n();
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ text: string; note: string } | null>(null);
  const createRef = useRef<HTMLDialogElement>(null);
  const secretRef = useRef<HTMLDialogElement>(null);

  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<string[]>(ALL_SCOPES);
  const [allowlist, setAllowlist] = useState('');
  const [busy, setBusy] = useState(false);

  async function reload() {
    try {
      setKeys(await api.listKeys());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  function showSecret(text: string, note: string) {
    setSecret({ text, note });
    secretRef.current?.showModal();
  }

  async function create() {
    setBusy(true);
    try {
      const created = await api.createKey({
        label,
        scopes,
        ip_allowlist: allowlist
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean),
      });
      createRef.current?.close();
      setLabel('');
      setAllowlist('');
      setScopes(ALL_SCOPES);
      showSecret(created.api_key, s.keys.secretNoteCreate);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function rotate(key: ApiKey) {
    const grace = window.prompt(s.keys.rotatePrompt, '24h');
    if (!grace || !(GRACE_CHOICES as readonly string[]).includes(grace)) return;
    try {
      const res = await api.rotateKey(key.key_id, grace as Grace);
      showSecret(
        res.api_key,
        s.keys.secretNoteRotate(fmt(res.old_key_valid_until, s.common.dash)),
      );
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function revoke(key: ApiKey) {
    // 吊销不可恢复，所以要求输入 key_id 确认 —— 比一个「确定吗？」
    // 更能防住误点，也让人真正看清自己要删的是哪一把。
    const typed = window.prompt(s.keys.revokePrompt(key.key_id));
    if (typed !== key.key_id) return;
    try {
      await api.revokeKey(key.key_id);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  const live = keys?.filter((k) => k.status !== 'revoked').length ?? 0;

  return (
    <>
      <h1>{s.keys.title}</h1>
      <p className="sub">
        {s.keys.subBefore}
        <code>{s.keys.authHeaderSample}</code>
        {s.keys.subAfter}
      </p>

      {error && <div className="banner danger">{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button
          className="btn btn-primary"
          onClick={() => createRef.current?.showModal()}
          disabled={live >= maxKeys}
        >
          {s.keys.create}
        </button>
        <span style={{ color: 'var(--ink-dim)', fontSize: 13.5 }}>
          {s.keys.count(live, maxKeys)}
        </span>
      </div>

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {keys === null ? (
          <div className="empty">{s.common.loading}</div>
        ) : keys.length === 0 ? (
          <div className="empty">{s.keys.empty}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{s.keys.colKey}</th>
                <th>{s.keys.colLabel}</th>
                <th>{s.keys.colStatus}</th>
                <th>{s.keys.colLastUsed}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.key_id}>
                  <td className="mono">
                    {k.masked}
                    {k.rotation_grace_until && (
                      <div style={{ color: 'var(--warn)', fontSize: 12, marginTop: 4 }}>
                        {s.keys.oldValidUntil(fmt(k.rotation_grace_until, s.common.dash))}
                      </div>
                    )}
                    {k.ip_allowlist.length > 0 && (
                      <div style={{ color: 'var(--ink-dim)', fontSize: 12, marginTop: 4 }}>
                        {s.keys.ipRestricted(k.ip_allowlist.join(', '))}
                      </div>
                    )}
                  </td>
                  <td>
                    {k.label || <span style={{ color: 'var(--ink-dim)' }}>{s.common.dash}</span>}
                  </td>
                  <td>
                    <span className={`pill ${k.status}`}>{s.keys.status[k.status]}</span>
                  </td>
                  <td className="mono" style={{ color: 'var(--ink-soft)' }}>
                    {fmt(k.last_used_at, s.common.dash)}
                    {k.last_used_ip && (
                      <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>{k.last_used_ip}</div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {k.status !== 'revoked' && (
                      <>
                        <button className="btn btn-sm" onClick={() => void rotate(k)}>
                          {s.keys.rotate}
                        </button>{' '}
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => void revoke(k)}
                        >
                          {s.keys.revoke}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {keys?.some((k) => k.status === 'suspended') && (
        <div className="banner" style={{ marginTop: 18 }}>
          {/* ⚠️ 这里原来写的是 Markdown 的 `**不是吊销**` —— JSX 不解析 Markdown，
              客户看到的是两侧带星号的字面量。用 <strong> 才真的加粗。 */}
          <strong>{s.keys.suspendedTitle}</strong> {s.keys.suspendedHintBefore}
          <strong>{s.keys.suspendedHintEmphasis}</strong>
          {s.keys.suspendedHintAfter}
        </div>
      )}

      {/* 创建弹窗 */}
      <dialog ref={createRef}>
        <div className="dlg-head">
          <h3>{s.keys.createTitle}</h3>
          <p>{s.keys.createSub}</p>
        </div>
        <div className="dlg-body">
          <div>
            <label htmlFor="label">{s.keys.labelField}</label>
            <input
              id="label"
              type="text"
              value={label}
              maxLength={64}
              placeholder={s.keys.labelPlaceholder}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div>
            <label>{s.keys.scopes}</label>
            <div className="checks">
              {ALL_SCOPES.map((scope) => (
                <label key={scope}>
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope)}
                    onChange={(e) =>
                      setScopes((prev) =>
                        e.target.checked ? [...prev, scope] : prev.filter((x) => x !== scope),
                      )
                    }
                  />
                  {scope}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label htmlFor="ips">{s.keys.ipField}</label>
            <input
              id="ips"
              type="text"
              value={allowlist}
              placeholder="203.0.113.0/24, 198.51.100.7/32"
              onChange={(e) => setAllowlist(e.target.value)}
            />
          </div>
        </div>
        <div className="dlg-foot">
          <button className="btn" onClick={() => createRef.current?.close()}>
            {s.common.cancel}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => void create()}
            disabled={busy || scopes.length === 0}
          >
            {busy ? s.keys.creating : s.keys.createAction}
          </button>
        </div>
      </dialog>

      {/* 明文展示弹窗 */}
      <dialog ref={secretRef}>
        <div className="dlg-head">
          <h3>{s.keys.secretTitle}</h3>
          <p>{secret?.note}</p>
        </div>
        <div className="dlg-body">
          <div className="secret">{secret?.text}</div>
          <button
            className="btn"
            onClick={() => secret && void navigator.clipboard.writeText(secret.text)}
          >
            {s.keys.copy}
          </button>
        </div>
        <div className="dlg-foot">
          <button
            className="btn btn-primary"
            onClick={() => {
              secretRef.current?.close();
              setSecret(null);
            }}
          >
            {s.keys.saved}
          </button>
        </div>
      </dialog>
    </>
  );
}
